import { isRecord, normalizeCart, readString, type StorefrontCart, type UnknownRecord } from "./types";
import { serializeCartRequest } from "./cart-queue";
import { trackCartMutation } from "./ga4-data-layer";

let latestCart: StorefrontCart | null = null;

declare global {
  interface WindowEventMap {
    "webshops:cart-updated": CustomEvent<StorefrontCart>;
  }
}

export { serializeCartRequest } from "./cart-queue";

export const DEFAULT_API_BASE_PATH = "/api/modules/webshops";

export class StorefrontApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly field?: string;

  constructor(message: string, status: number, code?: string, field?: string) {
    super(message);
    this.name = "StorefrontApiError";
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

export function cleanApiBasePath(value = DEFAULT_API_BASE_PATH): string {
  const trimmed = value.trim().replace(/\/+$/, "");
  return trimmed.startsWith("/") ? trimmed : DEFAULT_API_BASE_PATH;
}

export function cleanShopPath(value = "shop"): string {
  const path = value.trim().replace(/^\/+|\/+$/g, "");
  const segments = path.split("/");
  return segments.length > 0 && segments.every((segment) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/iu.test(segment))
    ? segments.join("/")
    : "shop";
}

async function parsedBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function apiError(response: Response, payload: unknown): StorefrontApiError {
  const root = isRecord(payload) ? payload : {};
  const error = isRecord(root.error) ? root.error : {};
  const details = isRecord(error.details) ? error.details : {};
  return new StorefrontApiError(
    readString(error, "message", "detail") ?? readString(root, "message") ?? `Request failed (${response.status}).`,
    response.status,
    readString(error, "code") ?? readString(root, "code"),
    readString(error, "field") ?? readString(details, "field") ?? readString(root, "field"),
  );
}

export function unwrapStorefrontData(value: unknown): unknown {
  return isRecord(value) && Object.prototype.hasOwnProperty.call(value, "data") ? value.data : value;
}

export async function storefrontGet(
  path: string,
  query: Record<string, string | string[] | number | boolean | undefined> = {},
  apiBasePath?: string,
  signal?: AbortSignal,
  extraHeaders: Record<string, string> = {},
): Promise<unknown> {
  const url = new URL(`${cleanApiBasePath(apiBasePath)}/${path.replace(/^\/+/, "")}`, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") for (const entry of Array.isArray(value) ? value : [value]) url.searchParams.append(key, String(entry));
  }
  const response = await fetch(url.pathname + url.search, {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json", ...extraHeaders },
    signal,
  });
  const payload = await parsedBody(response);
  if (!response.ok) throw apiError(response, payload);
  return unwrapStorefrontData(payload);
}

export async function storefrontMutation(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body: UnknownRecord,
  apiBasePath?: string,
  extraHeaders: Record<string, string> = {},
  query: Record<string, string | undefined> = {},
  signal?: AbortSignal,
): Promise<unknown> {
  const url = new URL(`${cleanApiBasePath(apiBasePath)}/${path.replace(/^\/+/, "")}`, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    if (value) url.searchParams.set(key, value);
  }
  const response = await fetch(url.pathname + url.search, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...extraHeaders,
    },
    body: JSON.stringify(body),
    signal,
  });
  const payload = await parsedBody(response);
  if (!response.ok) throw apiError(response, payload);
  return unwrapStorefrontData(payload);
}

export async function requestCart(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  body: UnknownRecord = {},
  itemId?: string,
  apiBasePath?: string,
  locale?: string,
): Promise<StorefrontCart> {
  return serializeCartRequest(async () => {
    const localizedBody = locale && body.locale === undefined
      ? { ...body, locale }
      : body;
    const payload =
      method === "GET"
        ? await storefrontGet("cart", locale ? { locale } : {}, apiBasePath)
        : await storefrontMutation("cart", method, localizedBody, apiBasePath, {}, itemId ? { itemId } : {});
    const cart = normalizeCart(payload);
    if (method !== "GET") trackCartMutation(method, { ...body, ...(itemId ? { itemId } : {}) }, latestCart, cart);
    latestCart = cart;
    // Publish inside the serialized request, before the next read/write can
    // complete. Every cart surface receives the same authoritative response.
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent("webshops:cart-updated", { detail: cart }));
    }
    return cart;
  });
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const random = Math.random().toString(36).slice(2);
  return `checkout-${Date.now().toString(36)}-${random}-${random}`;
}
